import { Pencil, RotateCcw, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Gravatar from 'react-gravatar'
import { toast } from 'react-hot-toast'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { DataTable, TableColumn } from '@/components/data-table/DataTable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export type UserLimitField = {
  name: string
  label: string
  placeholder: string
  unit?: string
  testId: string
  minValue?: number
}

export type UserLimitItem = {
  user_id: number
  email: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  [key: string]: any
}

type DialogState = 'none' | 'edit' | 'confirm' | 'reset' | 'add' | 'add-confirm'

type BaseUserLimitCardProps<T extends UserLimitItem> = {
  // Data and loading
  data: T[]
  isLoading: boolean
  isFetching: boolean
  hasNextPage: boolean

  // User management data
  orgUsersData?: {
    allUsers: Array<{ user_id: number; user?: { email: string } }>
  }

  // Form schemas and mutations
  editFormSchema: z.ZodType<any>
  addFormSchema: z.ZodType<any>
  updateMutation: {
    mutate: (
      data: any,
      options?: { onSuccess?: () => void; onError?: (error: any) => void }
    ) => void
    isPending: boolean
  }
  resetMutation: {
    mutate: (
      data: any,
      options?: { onSuccess?: () => void; onError?: (error: any) => void }
    ) => void
    isPending: boolean
  }

  // Configuration
  limitFields: UserLimitField[]
  defaultLimits: Record<string, number>

  // Table configuration
  tableColumns: (
    handleEditClick: (user: any) => void,
    handleResetClick: (user: any) => void
  ) => TableColumn<T>[]

  // Dialog configuration
  dialogConfig: {
    editTitle: string
    addTitle: string
    editDescription: string
    addDescription: string
    confirmTitle: string
    confirmDescription: (user: any) => string
    resetTitle: string
    resetDescription: (
      user: any,
      defaultLimits: Record<string, number>
    ) => string
    confirmPrompt: (user: any) => string
    warningPoints: string[]
    resetWarning: string
  }

  // Test IDs
  testIds: {
    addButton: string
    userSelectTrigger: string
    editButton: string
    resetButton: string
    cancelButton: string
    continueButton: string
  }

  // Custom field extraction
  extractUserLimits: (item: any) => Record<string, number>
  createMutationData: (userId: number, limits: Record<string, number>) => any
  resetMutationData: (userId: number) => any
}

export const BaseUserLimitCard = <T extends UserLimitItem>({
  data,
  isLoading,
  isFetching,
  hasNextPage,
  orgUsersData,
  editFormSchema,
  addFormSchema,
  updateMutation,
  resetMutation,
  limitFields,
  defaultLimits,
  tableColumns,
  dialogConfig,
  testIds,
  extractUserLimits,
  createMutationData,
  resetMutationData,
}: BaseUserLimitCardProps<T>) => {
  const [page, setPage] = useState(0)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [dialogState, setDialogState] = useState<DialogState>('none')

  const editForm = useForm({
    resolver: zodResolver(editFormSchema),
    defaultValues: Object.fromEntries(
      limitFields.map((field) => [field.name, 0])
    ),
  })

  const addForm = useForm({
    resolver: zodResolver(addFormSchema),
    defaultValues: {
      userId: '',
      ...Object.fromEntries(
        limitFields.map((field) => [field.name, defaultLimits[field.name] || 0])
      ),
    } as any,
  })

  const closeDialog = () => {
    setDialogState('none')
    setSelectedUser(null)
  }

  const onDialogChange = (open: boolean) => {
    if (!open) closeDialog()
  }

  const handleEditClick = (user: any) => {
    setSelectedUser(user)
    const limits = extractUserLimits(user)
    limitFields.forEach((field) => {
      editForm.setValue(field.name as any, limits[field.name])
    })
    setDialogState('edit')
  }

  const handleEditSubmit = (values: any) => {
    if (!selectedUser) return

    setSelectedUser({
      ...selectedUser,
      ...values,
    })
    setDialogState('confirm')
  }

  const handleResetClick = (user: any) => {
    setSelectedUser(user)
    setDialogState('reset')
  }

  const handleAddClick = () => {
    addForm.reset({
      userId: '',
      ...Object.fromEntries(
        limitFields.map((field) => [field.name, defaultLimits[field.name] || 0])
      ),
    } as any)
    setDialogState('add')
  }

  const handleAddSubmit = (values: any) => {
    const userId = parseInt(values.userId)
    const selectedUserOrg = orgUsersData?.allUsers.find(
      (userOrg) => userOrg.user_id === userId
    )

    if (!selectedUserOrg || !selectedUserOrg.user?.email) {
      toast.error('User information not found')
      setDialogState('none')
      return
    }

    const limits = Object.fromEntries(
      limitFields.map((field) => [field.name, values[field.name]])
    )

    setSelectedUser({
      id: userId,
      email: selectedUserOrg.user.email,
      ...limits,
    })

    setDialogState('add-confirm')
  }

  const handleConfirmUpdate = () => {
    if (!selectedUser) return

    const limits = Object.fromEntries(
      limitFields.map((field) => [field.name, selectedUser[field.name]])
    )

    updateMutation.mutate(createMutationData(selectedUser.id, limits), {
      onSuccess: () => {
        closeDialog()
        // Refetch would be called here in the parent component
      },
      onError: (error) => {
        toast.error(JSON.stringify(error))
        closeDialog()
      },
    })
  }

  const handleConfirmReset = () => {
    if (!selectedUser) return

    resetMutation.mutate(resetMutationData(selectedUser.id), {
      onSuccess: () => {
        closeDialog()
        // Refetch would be called here in the parent component
      },
      onError: (error) => {
        toast.error(JSON.stringify(error))
        closeDialog()
      },
    })
  }

  const columns = tableColumns(handleEditClick, handleResetClick)

  return (
    <div className="space-y-4">
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          onClick={handleAddClick}
          size="sm"
          data-testid={testIds.addButton}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add User Limit
        </Button>
      </div>

      <DataTable
        data={data || []}
        columns={columns}
        keyExtractor={(item) => item.user_id}
        isLoading={isLoading}
        isFetchingNextPage={isFetching && page > 0}
        hasNextPage={hasNextPage}
        fetchNextPage={() => setPage((p) => p + 1)}
        emptyStateMessage="No users with custom limits found"
      />

      {/* Edit Dialog */}
      <Dialog open={dialogState === 'edit'} onOpenChange={onDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogConfig.editTitle}</DialogTitle>
            <DialogDescription>
              {dialogConfig.editDescription}
            </DialogDescription>
          </DialogHeader>

          <Form {...editForm}>
            <form
              onSubmit={editForm.handleSubmit(handleEditSubmit)}
              className="space-y-4 py-4"
            >
              {limitFields.map((field) => (
                <FormField
                  key={field.name}
                  control={editForm.control}
                  name={field.name}
                  render={({ field: fieldData }) => (
                    <FormItem>
                      {limitFields.length > 1 && (
                        <FormLabel>{field.label}</FormLabel>
                      )}
                      <FormControl>
                        <Input
                          type="number"
                          {...fieldData}
                          value={fieldData.value === 0 ? '' : fieldData.value}
                          onChange={(e) => {
                            const value =
                              e.target.value === '' ? 0 : Number(e.target.value)
                            fieldData.onChange(value)
                          }}
                          placeholder={field.placeholder}
                          className="w-full"
                          data-testid={field.testId}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeDialog}
                  data-testid={testIds.cancelButton}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid={testIds.continueButton}>
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Add User Limit Dialog */}
      <Dialog open={dialogState === 'add'} onOpenChange={onDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogConfig.addTitle}</DialogTitle>
            <DialogDescription>{dialogConfig.addDescription}</DialogDescription>
          </DialogHeader>

          <Form {...addForm}>
            <form
              onSubmit={addForm.handleSubmit(handleAddSubmit)}
              className="space-y-4 py-4"
            >
              <FormField
                control={addForm.control}
                name="userId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select User</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid={testIds.userSelectTrigger}>
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {orgUsersData?.allUsers?.map((userOrg) => {
                          const hasCustomLimit = data?.some(
                            (item) => item.user_id === userOrg.user_id
                          )

                          return (
                            <SelectItem
                              key={userOrg.user_id}
                              value={userOrg.user_id.toString()}
                              disabled={hasCustomLimit}
                              data-testid={`user-option-${userOrg.user?.email}`}
                            >
                              {userOrg.user?.email || ''}
                              {hasCustomLimit && ' (already has custom limit)'}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {limitFields.map((field) => (
                <FormField
                  key={field.name}
                  control={addForm.control}
                  name={field.name}
                  render={({ field: fieldData }) => (
                    <FormItem>
                      <FormLabel>{field.label}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...fieldData}
                          value={fieldData.value === 0 ? '' : fieldData.value}
                          onChange={(e) => {
                            const value =
                              e.target.value === '' ? 0 : Number(e.target.value)
                            fieldData.onChange(value)
                          }}
                          placeholder={field.placeholder}
                          className="w-full"
                          data-testid={field.testId}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeDialog}
                  data-testid={testIds.cancelButton}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid={testIds.continueButton}>
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirm Update Dialog */}
      <ConfirmationDialog
        open={dialogState === 'confirm' || dialogState === 'add-confirm'}
        onOpenChange={onDialogChange}
        title={dialogConfig.confirmTitle}
        description={
          selectedUser ? dialogConfig.confirmDescription(selectedUser) : ''
        }
        onConfirm={handleConfirmUpdate}
        onCancel={closeDialog}
        buttons={{
          confirm: {
            label: updateMutation.isPending ? 'Applying...' : 'Apply Override',
            isLoading: updateMutation.isPending,
          },
          cancel: { label: 'Cancel' },
        }}
        confirmation={{
          text: selectedUser ? dialogConfig.confirmPrompt(selectedUser) : '',
          prompt: `Please type "${selectedUser ? dialogConfig.confirmPrompt(selectedUser) : ''}" to confirm:`,
        }}
        info={{
          warningPoints: dialogConfig.warningPoints,
        }}
      />

      {/* Confirm Reset Dialog */}
      <ConfirmationDialog
        open={dialogState === 'reset'}
        onOpenChange={onDialogChange}
        title={dialogConfig.resetTitle}
        description={
          selectedUser
            ? dialogConfig.resetDescription(selectedUser, defaultLimits)
            : ''
        }
        onConfirm={handleConfirmReset}
        onCancel={closeDialog}
        buttons={{
          confirm: {
            label: resetMutation.isPending
              ? 'Resetting...'
              : 'Reset to Default',
            isLoading: resetMutation.isPending,
            isDestructive: true,
          },
          cancel: { label: 'Cancel' },
        }}
        info={{
          alert: {
            title: 'Warning',
            description: dialogConfig.resetWarning,
          },
        }}
      />
    </div>
  )
}

// Helper component for user avatar display
export const UserAvatarDisplay = ({
  avatarUrl,
  firstName,
  lastName,
  email,
}: {
  avatarUrl?: string
  firstName?: string
  lastName?: string
  email: string
}) => (
  <div className="flex items-center gap-3">
    {avatarUrl ? (
      <img
        src={avatarUrl}
        alt={`${firstName || ''} ${lastName || ''}'s avatar`}
        className="h-10 w-10 rounded-full object-cover border border-border"
      />
    ) : (
      <Gravatar
        email={email || ''}
        className="h-10 w-10 rounded-full border border-border"
      />
    )}
    <div>
      <p className="font-medium">
        {firstName && lastName ? `${firstName} ${lastName}` : email || '--'}
      </p>
      <p className="text-sm text-foreground/70">{email}</p>
    </div>
  </div>
)

// Helper component for limit display
export const LimitDisplay = ({
  value,
  unit,
  color = 'primary',
}: {
  value: number | string
  unit: string
  color?: string
}) => (
  <div className="flex items-center gap-2">
    <span className={`w-2 h-2 rounded-full bg-${color}`}></span>
    <span className="font-medium">
      {value} {unit}
    </span>
  </div>
)

// Helper component for action buttons
export const UserLimitActions = ({
  onEdit,
  onReset,
  testIds,
}: {
  onEdit: () => void
  onReset: () => void
  testIds: { editButton: string; resetButton: string }
}) => (
  <div className="flex justify-end gap-2">
    <Button
      variant="outline"
      size="sm"
      onClick={onEdit}
      className="text-xs"
      data-testid={testIds.editButton}
    >
      <Pencil className="mr-1 h-3.5 w-3.5" />
      Edit
    </Button>
    <Button
      variant="outline"
      size="sm"
      onClick={onReset}
      className="text-xs text-destructive hover:text-destructive"
      data-testid={testIds.resetButton}
    >
      <RotateCcw className="mr-1 h-3.5 w-3.5" />
      Reset
    </Button>
  </div>
)
